import { createContext, useContext, useState, useCallback } from "react";

const ToastContext = createContext(null);

/**
 * Toast Provider Component
 * Wrap your app with this to enable toast notifications
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  /**
   * Add a new toast notification
   * @param {Object} options
   * @param {string} options.type - 'success' | 'error' | 'info' | 'warning'
   * @param {string} options.message - Message to display
   * @param {number} [options.duration=3000] - Duration in ms (0 = no auto-dismiss)
   */
  const showToast = useCallback((options) => {
    const id = Date.now() + Math.random();
    const toast = {
      id,
      type: options.type || "info",
      message: options.message,
      duration: options.duration !== undefined ? options.duration : 3000,
    };

    setToasts((prev) => [...prev, toast]);
  }, []);

  /**
   * Remove a toast by ID
   * @param {number} id - Toast ID to remove
   */
  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  /**
   * Remove all toasts
   */
  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const value = {
    toasts,
    showToast,
    removeToast,
    clearToasts,
  };

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

/**
 * Hook to access toast functionality
 * @returns {Object} Toast methods
 * @returns {Function} return.showToast - Show a toast notification
 * @returns {Function} return.clearToasts - Clear all toasts
 * 
 * @example
 * const { showToast } = useToast();
 * 
 * showToast({
 *   type: 'success',
 *   message: 'Operation completed!',
 *   duration: 3000
 * });
 */
export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }

  return {
    showToast: context.showToast,
    clearToasts: context.clearToasts,
  };
}
